import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { MuteRule, MuteRuleKind } from '@/types';

interface MuteStore {
    rules: MuteRule[];
    showMuted: boolean;

    addRule: (kind: MuteRuleKind, value: string) => void;
    removeRule: (id: string) => void;
    clearRules: () => void;
    setShowMuted: (showMuted: boolean) => void;
}

function createId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const useMuteStore = create<MuteStore>()(
    devtools(
        persist(
            (set) => ({
                rules: [],
                showMuted: false,

                addRule: (kind, value) =>
                    set((state) => {
                        const trimmedValue = value.trim();
                        if (!trimmedValue) return state;

                        const alreadyExists = state.rules.some((rule) => rule.kind === kind && rule.value === trimmedValue);
                        if (alreadyExists) return state;

                        const nextRule: MuteRule = {
                            id: createId(),
                            kind,
                            value: trimmedValue,
                            createdAt: new Date().toISOString()
                        };

                        return { rules: [nextRule, ...state.rules] };
                    }),

                removeRule: (id) => set((state) => ({ rules: state.rules.filter((rule) => rule.id !== id) })),

                clearRules: () => set({ rules: [] }),

                setShowMuted: (showMuted) => set({ showMuted })
            }),
            {
                name: 'logwatch-mute-rules',
                version: 1
            }
        ),
        { name: 'mute-store' }
    )
);
